import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../utils/formatters';

export const GlassCard = ({
  children,
  className,
  variant = 'default',
  hover = true,
  animate = true,
  glow = false,
  glowColor = 'primary',
  ...props
}) => {
  const variants = {
    default: 'bg-slate-900/60 backdrop-blur-xl border-white/10',
    elevated: 'bg-slate-900/80 backdrop-blur-2xl border-white/15 shadow-glass-lg',
    outlined: 'bg-transparent backdrop-blur-md border-white/20',
    subtle: 'bg-slate-900/30 backdrop-blur-lg border-white/5',
  };

  const glowColors = {
    primary: 'hover:shadow-glow',
    success: 'hover:shadow-glow-success',
    warning: 'hover:shadow-glow-warning',
    danger: 'hover:shadow-glow-danger',
  };

  const baseClasses = cn(
    'relative rounded-2xl border overflow-hidden',
    variants[variant],
    hover && 'transition-all duration-300 hover:-translate-y-1 hover:border-white/20',
    glow && glowColors[glowColor],
    className
  );

  if (animate) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
        className={baseClasses}
        {...props}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <div className={baseClasses} {...props}>
      {children}
    </div>
  );
};

export const GlassCardHeader = ({ children, className, icon: Icon, title, subtitle }) => (
  <div className={cn('px-6 py-5 border-b border-white/10', className)}>
    <div className="flex items-center gap-3">
      {Icon && (
        <div className="p-2 rounded-xl bg-gradient-to-br from-accent-primary/20 to-accent-secondary/20">
          <Icon className="w-5 h-5 text-accent-primary" />
        </div>
      )}
      <div>
        {title && <h3 className="text-lg font-semibold text-white/90">{title}</h3>}
        {subtitle && <p className="text-sm text-white/50">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

export const GlassCardBody = ({ children, className }) => (
  <div className={cn('p-6', className)}>
    {children}
  </div>
);

export const GlassCardFooter = ({ children, className }) => (
  <div className={cn('px-6 py-4 border-t border-white/10 bg-white/[0.02]', className)}>
    {children}
  </div>
);

export default GlassCard;
